"use client";

import type React from "react";
import { useState, useEffect, type ChangeEvent } from "react";
import { dictionary } from "../../../utils/dictionary";
import { useAuth } from "../../../context/AuthContext";
import { formatCOP } from "../../../utils/formatters";
import { UploadedMethodType } from "../../../store/onboardingStore";
import { ProductVariantsInfo } from "./ProductVariantsInfo";

export type BaseProductConfigItem = {
  name?: string;
  price: number;
  imageUrl: string;
  uploadedMethod?: UploadedMethodType;
  variantsAttributes?: string[] | null;
};

export type UpsellConfigItem = BaseProductConfigItem & {
  id: string;
};

export interface PriceConfigurationProps {
  mainProduct?: BaseProductConfigItem;
  onMainProductPriceChange?: (newPrice: number) => void;


  bumpOffer?: BaseProductConfigItem;
  onBumpOfferPriceChange?: (newPrice: number) => void;

  upsells?: UpsellConfigItem[];
  onUpsellPriceChange?: (id: string, newPrice: number) => void;
}

export interface PriceInputRowProps {
  productLabel: string;
  label: string;
  imageUrl: string;
  price: number;
  onPriceChange: (newPrice: number) => void;
  inputId: string;
  disabled?: boolean;
}

const PriceInputRow: React.FC<PriceInputRowProps> = ({
  productLabel,
  label,
  imageUrl,
  price,
  onPriceChange,
  inputId,
  disabled,
}) => {
  const [inputValue, setInputValue] = useState<string>(price.toFixed(2));

  useEffect(() => {
    onPriceChange(Number(inputValue));
  }, []);

  const removeSpecialCharacters = (text: string): string => {
    return text.replace(/[^a-zA-Z0-9 ]/g, "");
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newStringValue = removeSpecialCharacters(event.target.value);
    setInputValue(newStringValue);
    onPriceChange(Number(newStringValue));
  };

  const handleBlur = () => {
    const numericValue = Number.parseFloat(inputValue);
    if (!isNaN(numericValue)) {
      const formattedValue = formatCOP(numericValue).slice(1);
      setInputValue(formattedValue);
      if (
        Math.abs(numericValue - price) > 0.001 ||
        formattedValue !== price.toFixed(2)
      ) {
        onPriceChange(numericValue);
      }
    } else {
      setInputValue(price.toFixed(2));
      if (0 !== price) {
        onPriceChange(price);
      }
    }
  };

  return (
    <div className="flex items-center gap-4 py-3">
      <img
        src={
          imageUrl || "/placeholder.svg?height=64&width=64&query=product+image"
        }
        alt={label || "Product image"}
        width={64}
        height={64}
        className="w-16 h-16 bg-slate-200 rounded-md object-cover flex-shrink-0"
      />
      <div className="flex-grow space-y-1">
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-slate-700"
        >
          <strong>{productLabel}:</strong> {label}
        </label>
        <div className="relative rounded-md shadow-sm">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <span className="text-slate-500 sm:text-sm">$</span>
          </div>
          <input
            type="text"
            id={inputId}
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder="0.00"
            className="block w-full rounded-md border-0 py-1.5 pl-7 pr-3 text-slate-900 ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-500 sm:text-sm sm:leading-6"
            inputMode="decimal"
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
};

export const PriceConfiguration: React.FC<PriceConfigurationProps> = ({
  mainProduct,
  onMainProductPriceChange,

  bumpOffer,
  onBumpOfferPriceChange,
  upsells,
  onUpsellPriceChange,
}) => {
  const { language } = useAuth();

  return (
    <div className="w-full bg-white border border-slate-200 rounded-lg">
      <div className="px-4 py-5 sm:px-6 border-b border-slate-200">
        <h2 className="text-lg font-medium leading-6 text-slate-900">
          {dictionary["price-configuration"][language]}
        </h2>
      </div>

      <div className="px-4 py-5 sm:p-6">
        <div className="space-y-3 divide-y divide-slate-200">
          {mainProduct && onMainProductPriceChange && mainProduct.name && (
            <div className="pt-3 first:pt-0">
              <PriceInputRow
                productLabel={dictionary["main-product"][language]}
                label={mainProduct.name}
                imageUrl={mainProduct.imageUrl}
                price={mainProduct.price}
                onPriceChange={onMainProductPriceChange}
                inputId="main-product-price"
                disabled={
                  mainProduct.uploadedMethod === UploadedMethodType.PREVIOUS
                }
              />
              <ProductVariantsInfo 
                variantsAttributes={mainProduct.variantsAttributes || []}
                language={language}
              />
            </div>
          )}

          {bumpOffer && onBumpOfferPriceChange && bumpOffer.name && (
            <div className="pt-3 first:pt-0">
              <PriceInputRow
                productLabel={dictionary["order-bump"][language]}
                label={bumpOffer.name}
                imageUrl={bumpOffer.imageUrl}
                price={bumpOffer.price}
                onPriceChange={onBumpOfferPriceChange}
                inputId="order-bump-price"
                disabled={
                  bumpOffer.uploadedMethod === UploadedMethodType.PREVIOUS
                }
              />
            </div>
          )}

          {upsells &&
            upsells.length > 0 &&
            onUpsellPriceChange &&
            upsells.map((upsell, index) => (
              <div className="pt-3 first:pt-0" key={upsell.id}>
                <PriceInputRow
                  productLabel={dictionary["upsell-offer"][language]}
                  label={upsell.name || `Upsell Offer ${index + 1}`}
                  imageUrl={upsell.imageUrl}
                  price={
                    upsell.uploadedMethod === UploadedMethodType.PREVIOUS
                      ? Number(upsell.price)
                      : 0
                  }
                  onPriceChange={(newPrice) =>
                    onUpsellPriceChange(upsell.id, newPrice)
                  }
                  inputId={`upsell-offer-price-${upsell.id}`}
                  disabled={
                    upsell.uploadedMethod === UploadedMethodType.PREVIOUS
                  }
                />
              </div>
            ))}

          {!mainProduct && !bumpOffer && (!upsells || upsells.length === 0) && (
            <p className="text-sm text-slate-500 py-4 text-center">
              {dictionary["no-products-configured"][language]}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PriceConfiguration;
